import type { WorkspaceRole } from '@/modules/workspaces/types';

/**
 * Single source of truth for "how many billable seats does this workspace use."
 * Counts all members with any role except `observer`, plus pending invitations for
 * non-observer roles. The owner counts toward the total.
 *
 * Used by SettingsSection, PeopleSection, and edge functions (checkout/portal-session) so a
 * future "coach" seat exemption is a one-place change. Do not re-derive this elsewhere.
 */
export function countBillableSeats(
  members: { role: WorkspaceRole }[],
  invitations: { role: WorkspaceRole; status: string }[],
): number {
  const memberSeats = members.filter((m) => m.role !== 'observer').length;
  const pendingInviteSeats = invitations.filter(
    (i) => i.status === 'pending' && i.role !== 'observer',
  ).length;
  return memberSeats + pendingInviteSeats;
}

/** Result of checking whether a workspace has room for `additionalSeats` more billable seats. */
export interface SeatCapCheck {
  currentSeats: number;
  cap: number | null;
  /** True if currentSeats + additionalSeats would exceed cap. Always false when cap is null. */
  atCap: boolean;
}

/**
 * Checks a prospective seat addition against a limit (null = uncapped). Callers pass the
 * workspace's currently *purchased* seat quantity here (the owner-controlled, self-serve number
 * from Stripe), not the cohort component's `cap` field — seat provisioning is a billing decision
 * the owner makes separately (via Stripe's Customer Portal); invites just consume from that pool.
 */
export function checkSeatCap(
  members: { role: WorkspaceRole }[],
  invitations: { role: WorkspaceRole; status: string }[],
  cap: number | null,
  additionalSeats = 1,
): SeatCapCheck {
  const currentSeats = countBillableSeats(members, invitations);
  const atCap = cap != null && currentSeats + additionalSeats > cap;
  return { currentSeats, cap, atCap };
}
